import { useTranslation } from "@shared/utils/internationalization";
import CurrencyFormat from "@ui-kit/data-display/CurrencyFormat/CurrencyFormat.web";
import Button from "@ui-kit/form/Button/Button.web";
import NumericFormat from "@ui-kit/form/NumericFormat/NumericFormat.web";
import Box from "@ui-kit/layout/Box/Box.web";
import Flex from "@ui-kit/layout/Flex/Flex.web";
import HighlightBox from "@ui-kit/layout/HighlightBox/HighlightBox.web";
import Text from "@ui-kit/typography/Text/Text.web";
import { getCreditLabels } from "../../utils/discount-utils";
import {
  DiscountItem,
  DiscountRegularCreditItemProps,
  DiscountRegularCreditSharedProps,
  DiscountSharedProps,
} from "./Discount.props";

export default function Discount(props: DiscountProps): JSX.Element {
  return <Discount.Single {...props} />;
}

Discount.Item = function DiscountItem(props: DiscountItem) {
  const {
    code,
    amount,
    percentage,
    currency,
    application,
    limit,
    description,
    button,
    applied,
    "data-testid": dataTestId = "discount",
  } = props;
  const { t } = useTranslation("discounts");

  return (
    <Flex
      p="xs"
      gap="xs"
      justify="space-between"
      alignItems="center"
      width="100%"
    >
      <Box flexBasis="fit-content">
        <Text
          size="sm"
          fontWeight={700}
          lineHeight="17px"
          wordBreak="break-word"
        >
          <Text color="green.900" as="span" data-testid={`${dataTestId}-value`}>
            {amount ? (
              <CurrencyFormat
                displayType="text"
                value={amount}
                currency={currency}
                // show decimal part if number has it
                decimalScale={amount % 1 !== 0 ? 2 : 0}
              />
            ) : null}
            {percentage ? (
              <NumericFormat
                displayType="text"
                value={percentage}
                // show decimal part if number has it
                decimalScale={percentage % 1 !== 0 ? 2 : 0}
                suffix="%"
                fixedDecimalScale
              />
            ) : null}
          </Text>
          {application && (
            <>
              <Text
                color="green.900"
                as="span"
                data-testid={`${dataTestId}-application`}
              >
                {` `}
                {application}
              </Text>
            </>
          )}
          {limit && (
            <Text
              color="green.900"
              as="span"
              data-testid={`${dataTestId}-limit`}
            >
              {` `}({limit})
            </Text>
          )}
        </Text>
        {code && (
          <Text color="green.900" size="xs" wordBreak="break-all">
            {t("Code")}
            {`: `}
            <Text
              as="span"
              color="green.900"
              data-testid={`${dataTestId}-code`}
            >
              {code}
            </Text>
          </Text>
        )}
        {description && (
          <Text
            color="green.900"
            size="xs"
            wordBreak="break-all"
            data-testid="description"
          >
            {description}
          </Text>
        )}
        {applied && (
          <Text color="green.900" size="xs">
            {applied}
          </Text>
        )}
      </Box>
      {button}
    </Flex>
  );
};

Discount.Single = function DiscountSingle(props: DiscountProps) {
  const { onClick, isDisabled, ...rest } = props;
  const { t } = useTranslation("discounts");

  return (
    <HighlightBox
      statusColor="green"
      justify="space-between"
      border="none"
      p="none"
    >
      <Discount.Item
        applied={t("Discount applied")}
        button={
          onClick && (
            <Button.Link
              p="4px 8px"
              color="green.900"
              size="sm"
              fontWeight="500"
              onClick={onClick}
              isDisabled={isDisabled}
              aria-label={t("remove discount")}
              data-testid="remove-discount-code"
            >
              {t("Remove")}
            </Button.Link>
          )
        }
        {...rest}
      />
    </HighlightBox>
  );
};

Discount.AccountCredit = function DiscountAccountCredit(props: DiscountProps) {
  const { onClick, isDisabled, ...rest } = props;
  const { t } = useTranslation("discounts");

  return (
    <HighlightBox
      statusColor="green"
      justify="space-between"
      border="none"
      p="none"
    >
      <Discount.Item
        button={
          onClick && (
            <Button.Link
              p="4px 8px"
              color="green.900"
              size="sm"
              fontWeight="500"
              onClick={onClick}
              isDisabled={isDisabled}
              aria-label={t("remove credit")}
              data-testid="remove-credit"
            >
              {t("Remove")}
            </Button.Link>
          )
        }
        applied={t("Credit applied")}
        data-testid="credit"
        {...rest}
      />
    </HighlightBox>
  );
};

Discount.ExchangeCredit = function DiscountExchangeCredit(
  props: DiscountProps
) {
  const { hasExchangeCredit, onClick, isDisabled, ...rest } = props;
  const { t } = useTranslation("discounts");

  return (
    <HighlightBox
      statusColor="green"
      justify="space-between"
      borderColor={hasExchangeCredit ? "transparent" : "green.900"}
      bg={hasExchangeCredit ? "green.200" : "white"}
      p="none"
    >
      <Discount.Item
        button={
          onClick && (
            <Button.Link
              p="4px 8px"
              color="green.900"
              size="sm"
              fontWeight="500"
              onClick={onClick}
              isDisabled={isDisabled}
              _hover={{
                color: "green.900",
                textDecoration: "none",
              }}
              aria-label={
                hasExchangeCredit ? t("remove credit") : t("apply credit")
              }
              data-testid={
                hasExchangeCredit
                  ? "remove-exchange-credit"
                  : "apply-exchange-credit"
              }
            >
              {hasExchangeCredit ? t("Remove") : t("Apply")}
            </Button.Link>
          )
        }
        applied={
          hasExchangeCredit
            ? t("Exchange credit applied")
            : t("Available exchange credit")
        }
        data-testid="exchange-credit"
        {...rest}
      />
    </HighlightBox>
  );
};

Discount.CreditIsAvailable = function DiscountCreditIsAvailable(
  props: DiscountProps
) {
  const { onClick, ...rest } = props;
  const { t } = useTranslation("discounts");

  return (
    <HighlightBox
      statusColor="green"
      justify="space-between"
      borderColor="green.900"
      bg="bg"
      p="none"
    >
      <Discount.Item
        applied={
          <Text as="span" size="sm" fontWeight={700} lineHeight="17px">
            {t("Credits available")}
          </Text>
        }
        button={
          onClick && (
            <Button.Link
              p="4px 8px"
              color="green.900"
              size="sm"
              fontWeight="500"
              onClick={onClick}
              aria-label={t("view credits")}
              data-testid="view-credits"
            >
              {t("View credits")}
            </Button.Link>
          )
        }
        {...rest}
      />
    </HighlightBox>
  );
};

Discount.RegularCredit = function DiscountRegularCredit(
  props: DiscountRegularCreditSharedProps
) {
  return <Discount.RegularCreditSingle {...props} />;
};

Discount.RegularCreditItem = function DiscountRegularCreditItem(
  props: DiscountRegularCreditItemProps
) {
  const {
    amount,
    currency,
    button,
    applied,
    "data-testid": dataTestId = "gift-card",
  } = props;

  return (
    <Flex
      p="xs"
      gap="xs"
      justify="space-between"
      alignItems="center"
      width="100%"
    >
      <Box flexBasis="fit-content">
        <Text
          size="sm"
          fontWeight={700}
          lineHeight="17px"
          wordBreak="break-word"
        >
          <Text color="green.900" as="span" data-testid={`${dataTestId}-value`}>
            {amount ? (
              <CurrencyFormat
                displayType="text"
                value={amount}
                currency={currency}
                // show decimal part if number has it
                decimalScale={amount % 1 !== 0 ? 2 : 0}
              />
            ) : null}
          </Text>
        </Text>
        {applied && (
          <Text color="green.900" size="xs">
            {applied}
          </Text>
        )}
      </Box>
      {button}
    </Flex>
  );
};

Discount.RegularCreditSingle = function DiscountRegularCreditSingle(
  props: DiscountRegularCreditSharedProps
) {
  const {
    onClick,
    isDisabled,
    isApplied,
    amount,
    creditApplied,
    creditType,
    ...rest
  } = props;

  const { t } = useTranslation("discounts");

  const { appliedText, ariaLabel, testId } = getCreditLabels(
    isApplied,
    creditType
  );

  if (!amount) {
    return <></>;
  }
  return (
    <HighlightBox
      statusColor={isApplied ? "green" : "white"}
      justify="space-between"
      border="1px solid"
      borderColor={isApplied ? "white" : "green"}
      p="none"
    >
      <Discount.RegularCreditItem
        amount={isApplied ? creditApplied : Number(amount)}
        applied={appliedText}
        button={
          onClick && (
            <Button.Link
              p="4px 8px"
              color="green.900"
              size="sm"
              fontWeight="500"
              onClick={() => onClick(amount ?? 0)}
              isDisabled={isDisabled}
              aria-label={ariaLabel}
              data-testid={testId}
            >
              {isApplied ? t("Remove") : t("Apply")}
            </Button.Link>
          )
        }
        {...rest}
      />
    </HighlightBox>
  );
};

export type DiscountProps = DiscountSharedProps & {
  // Add any web specific props here.
};
